// @flow
import React, { memo } from 'react';
import { TableBody, TableCell, Checkbox, useMediaQuery } from '@material-ui/core';
import { ScrollLeft as ScrollLeftIcon, ScrollRight as ScrollRightIcon } from 'components/icons';
import { LazyIcon, Typography, DashboardBoxTitle } from 'components';
import { hasKey } from 'helpers';
import { useTableRowSelected, useRecord } from 'hooks';
import FilterTable from './FilterTable';
import ShowingHeadCell from './ShowingHeadCell';
import {
  TableHeadContent,
  TableRowContent,
  TableSort,
  TableHeadIconMobile,
  TableHeadContentSubLabel
} from './ComponentTypes';

type TableHeadTypes = {
  numSelected: number,
  onRequestSort: Function,
  onSelectAllClick: Function,
  order: 'asc' | 'desc',
  orderBy: string,
  rowCount: number,
  tableSettings: Object,
  isFilter: boolean,
  onSearch: Function,
  showPassword: Function,
  record: any
};

const TableHead = ({
  onSelectAllClick,
  order,
  orderBy,
  numSelected,
  rowCount,
  onRequestSort,
  tableSettings,
  isFilter,
  onSearch,
  showPassword,
  record
}: TableHeadTypes) => {
  const { isSelectedAll } = useTableRowSelected();
  const { cells, selecting, folding } = tableSettings;

  const createSortHandler = property => event => {
    onRequestSort(event, property);
  };

  const isRadio = hasKey(tableSettings, 'selectingRadio') && tableSettings.selectingRadio;

  const productName = useRecord('user', 'profile', 'product_name');

  const newCells = cells.filter(c => {
    return !(rowCount === 0 && (c?.hideWhenNoResult || c?.hideWhenProducts?.includes(productName)) );
  });

  const hiddenCell = useMediaQuery(theme => theme.breakpoints.up('sm'));

  return (
    <>
      <TableHeadContent>
        <TableRowContent>
          {!hiddenCell && (
            <TableHeadIconMobile padding="checkbox">
              <ScrollLeftIcon size={1.7} ml={2} mr={2} />
            </TableHeadIconMobile>
          )}
          {selecting && (
            <TableCell align="left" padding="checkbox" id="fistElement">
              {!isRadio && (
                <Checkbox
                  indeterminate={numSelected > 0 && numSelected < rowCount}
                  checked={isSelectedAll || numSelected === rowCount}
                  onChange={onSelectAllClick}
                  inputProps={{ 'aria-label': 'select all desserts' }}
                />
              )}
            </TableCell>
          )}
          {folding && <TableCell align="left" padding="checkbox" />}
          {newCells.map(headCell => (
            <TableCell
              align={headCell.labelAlign || 'left'}
              key={headCell.id}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              <TableSort
                active={orderBy === headCell.id}
                direction={order}
                onClick={createSortHandler(headCell.ghostId || headCell.id)}
                disabled={headCell.hideOrderBy}
                labelPaddingLeft={headCell.labelPaddingLeft}
              >
                {headCell.renderIconLabel ? (
                  <LazyIcon component={headCell.renderIconLabel} size={1.5} ml={1.5}  color="colorDefault" />
                ) : (
                  <>
                    {headCell.infoIcon ? (
                      <DashboardBoxTitle
                        icon
                        label={headCell.label}
                        mbbox
                        fullWidth
                        fontSize={14}
                        iconSize={1}
                        description={headCell.infoIconDescription}
                        labelColor="var(--colorFontTableHeader)"
                      />
                    ) : (
                      headCell.label
                    )}
                  </>
                )}
                {orderBy === headCell.id ? <span>{order === 'desc' ? '' : ''}</span> : null}
              </TableSort>
              <br />
              {rowCount !== 0 && headCell.subLabel && (
                <TableHeadContentSubLabel>
                  {headCell.subLabel.map(label => (
                    <Typography.p fontSize={12} mt={0.1} mb={0.1}>
                      {label}
                    </Typography.p>
                  ))}
                </TableHeadContentSubLabel>
              )}
              {headCell.showing && (
                <ShowingHeadCell
                  optionDefault={headCell.defaultShowingValue}
                  options={headCell.showingValue}
                  showPassword={showPassword}
                />
              )}
            </TableCell>
          ))}
          {!hiddenCell && (
            <TableHeadIconMobile padding="checkbox">
              <ScrollRightIcon size={1.7} ml={2} mr={2} />
            </TableHeadIconMobile>
          )}
        </TableRowContent>
      </TableHeadContent>
      {isFilter && (
        <TableBody>
          <TableRowContent>
            {selecting && <TableCell align="left" />}
            {folding && <TableCell align="left" />}
            {newCells.map(cell => (
              <TableCell align="left" id="lastElement">
                <FilterTable dataFilter={cell.filter} onFilter={onSearch} onSearch={onSearch} />
              </TableCell>
            ))}
          </TableRowContent>
        </TableBody>
      )}
    </>
  );
};

export default memo<TableHeadTypes>(TableHead);
